// frontend/src/samkalp-faculty/SamkalpFacultyDashboard.js
//
// Faculty Dashboard shell. Two mutually-exclusive layouts sharing one
// sidebar column — never two nested sidebars at once:
//
// 1. Dashboard / Live Sessions: the normal shell below (outer nav +
//    main content area).
// 2. Teaching Copilot: TeachingCopilot.js already renders its own
//    complete, self-contained layout (own 100vh wrapper, own course/unit
//    sidebar, own main content) — it's used standalone at
//    /tenant/:slug/copilot for every other tenant, so we deliberately do
//    NOT modify it. Instead, when the route is under .../copilot, this
//    shell steps aside entirely and lets TeachingCopilot's own sidebar
//    BE the visible sidebar (same screen column, not a nested box), with
//    a thin fixed strip overlaid on top for navigation back out — this
//    keeps the "one sidebar at a time" feel without touching a component
//    other tenants depend on.
//
// Live Sessions is deliberately still a normal first-class nav item here
// (not folded into Teaching Copilot) since it's expected to extend to
// other tenants later, same as Teaching Copilot.
import React from 'react';
import { Routes, Route, NavLink, useLocation, useNavigate } from 'react-router-dom';
import FacultyDashboardHome from './FacultyDashboardHome';
import MySessionsPage from './liveSessions/MySessionsPage';
import MyMentoringSlotsPage from './mentoring/MyMentoringSlotsPage';
import TeachingCopilot from '../components/Projects/TeachingCopilot';
import './SamkalpFacultyDashboard.css';

const getNavItems = (slug) => [
    { key: 'dashboard', label: 'Dashboard', path: '' },
    { key: 'live-sessions', label: 'Live Sessions', path: 'live-sessions' },
    { key: 'mentoring', label: '1:1 Mentoring', path: 'mentoring' },
    { key: 'copilot', label: 'Teaching Copilot', path: `copilot/${slug}` },
];

const SamkalpFacultyDashboard = ({ slug, onLogout }) => {
    const navItems = getNavItems(slug);
    const location = useLocation();
    const navigate = useNavigate();
    const inCopilot = location.pathname.startsWith('/samkalp-ias/faculty/copilot');

    if (inCopilot) {
        // TeachingCopilot sets its own root div to height:'100vh' (a viewport
        // unit, unaffected by any parent constraint) — so this bar is a pure
        // overlay, not a reserved-space header. Reserving space with padding
        // would push TeachingCopilot down while it still claims a full 100vh,
        // clipping 32px off its bottom instead. An overlay only costs a small
        // visual overlap with TeachingCopilot's own top-left brand label,
        // which isn't interactive, vs. clipping something that might be.
        return (
            <div style={{ position: 'relative', height: '100vh' }}>
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, height: 28, zIndex: 2000,
                        background: '#092957', color: '#fff', display: 'flex', alignItems: 'center',
                        padding: '0 14px', fontSize: 11, gap: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                >
                    <button
                        type="button"
                        onClick={() => navigate('/samkalp-ias/faculty')}
                        style={{ background: 'transparent', border: 'none', color: '#f1a51c', fontWeight: 700, fontSize: 11, cursor: 'pointer', padding: 0 }}
                    >
                        ← Samkalp Dashboard
                    </button>
                    <span style={{ opacity: 0.4 }}>|</span>
                    <NavLink to="/samkalp-ias/faculty/live-sessions" style={{ color: '#c7d4ea', fontSize: 11 }}>
                        Live Sessions
                    </NavLink>
                </div>
                <Routes>
                    <Route path="copilot/:slug" element={<TeachingCopilot />} />
                </Routes>
            </div>
        );
    }

    return (
        <div className="samkalp-faculty-shell">
            <aside className="samkalp-faculty-sidebar">
                <div className="samkalp-faculty-brand">
                    <div className="samkalp-faculty-brand-sun">☀</div>
                    <div>
                        <strong>SAMKALP</strong>
                        <span>Faculty Portal</span>
                    </div>
                </div>

                <nav className="samkalp-faculty-nav">
                    {navItems.map(item => (
                        <NavLink
                            key={item.key}
                            to={item.path ? `/samkalp-ias/faculty/${item.path}` : '/samkalp-ias/faculty'}
                            end={item.path === ''}
                            className={({ isActive }) => `samkalp-faculty-nav-link ${isActive ? 'active' : ''}`}
                        >
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="samkalp-faculty-sidebar-footer">
                    <div className="samkalp-faculty-slug">{slug}</div>
                    <button className="samkalp-faculty-logout-btn" onClick={onLogout}>Log out</button>
                </div>
            </aside>

            <main className="samkalp-faculty-main">
                <Routes>
                    <Route index element={<FacultyDashboardHome slug={slug} />} />
                    <Route path="live-sessions" element={<MySessionsPage slug={slug} />} />
                    <Route path="mentoring" element={<MyMentoringSlotsPage slug={slug} />} />
                </Routes>
            </main>
        </div>
    );
};

export default SamkalpFacultyDashboard;
